
import React, { useState } from 'react';

import Slide from '@mui/material/Slide';
import { Box, styled } from "@mui/system";

import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue } from 'recoil';


import TextField from '@mui/material/TextField';

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from '@mui/material/IconButton';
import { BasicExportForm } from './ui/BasicExportForm';
import { IFeatureDescription } from 'library/interface';
import { HeadCell } from './ui/EnhancedTable';
import { Height } from '@mui/icons-material';

import useAxios from 'library/axios';

import { currentUserSessionAtom, isExportBoxShowAtom, currentBasicTextFilterPropsAtom } from 'library/store';
import { FormDialog } from './ui/FormDialog';
import { Button, Checkbox, FormControlLabel, MenuItem, Stack } from '@mui/material';
import { setConstantValue } from 'typescript';
import { useSnackbar } from 'notistack';


export interface UploadFormDialogProps {
  open: boolean,
  onClose: () => void,

  //folderName: string

  // entityId: number,
  // entityName: string,

}



interface UploadStatus {
  uploadedChunks: number[];
}

const CHUNK_SIZE = 1 * 1024 * 1024; // 1 MB
const PARALLEL_LIMIT = 5; // number of concurrent uploads


export function UploadFormDialog(props: UploadFormDialogProps) {

  const { open, onClose } = props;
  const { t, i18n } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const axios = useAxios();

  const { language, userId } = useRecoilValue(currentUserSessionAtom);

  const [fileType, setFileType] = useState('');
  const [reportName, setReportName] = useState('');

  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const [isPrivate, setIsPrivate] = useState<boolean>(false);



  const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

  // 🔹 Resolve file information dynamically
  const getFileConfig = () => {
    switch (fileType) {
      case 'backend':
        return { folderName: 'uploaded-app', fileName: 'KampuxAPI.zip', downloadName: 'KampuxAPI.zip' };
      case 'frontend':
        return { folderName: 'uploaded-app', fileName: 'KampuxWEB.zip', downloadName: 'KampuxAPI.zip' };
      case 'report':
        return {
          folderName: 'uploaded-reports',
          fileName: 'xxxx.zip',
          downloadName: reportName ? `${reportName}.zip` : 'report.zip',
        };
      case 'other':
        return {
          folderName: 'uploaded-files',
          fileName: 'xxxx.zip',
          downloadName: reportName ? `${reportName}.zip` : 'report.zip',
        };
      default:
        throw new Error('Please select a file type.');
    }
  };


  // 🔹 File input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
  };

  // 🔹 Upload a single chunk
  const uploadChunk = async (
    file: File,
    index: number,
    totalChunks: number
  ): Promise<void> => {
    const chunk = file.slice(index * CHUNK_SIZE, (index + 1) * CHUNK_SIZE);

    const { folderName, fileName, downloadName } = getFileConfig();

    const formData = new FormData();

    formData.append("chunk", chunk);
    formData.append("index", index.toString());
    formData.append("totalChunks", totalChunks.toString());
    formData.append("fileName", file.name);
    formData.append("folderName", folderName);
    //formData.append("entityId", entityId.toString());
    formData.append("subFolder", isPrivate ?  userId.toString() : '');

    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await axios.post("/api/home/upload-chunk", formData, {
          onUploadProgress: (event) => {
            if (event.total) {
              const percent = Math.round((event.loaded / event.total) * 100);
              setStatus(`Chunk ${index + 1}: ${percent}%`);
            }
          },
        });
        return; // success
      } catch (err) {
        console.warn(`Retrying chunk ${index} (attempt ${attempt + 1})...`);
        await sleep(1000 * (attempt + 1));
      }
    }

    throw new Error(`Failed to upload chunk ${index}`);
  };


  // 🔹 Full upload process
  const uploadFile = async (file: File) => {
    setIsUploading(true);
    setProgress(0);
    setStatus("Initializing upload...");

    const { folderName, fileName } = getFileConfig();
    if( (fileType === 'backend' && file.name !== 'KampuxAPI.zip') || 
        (fileType === 'frontend' && file.name !== 'KampuxWEB.zip') ||
        (fileType === 'report' && !file.name.startsWith('Report') && !file.name.endsWith('.zip')) ) {
          enqueueSnackbar(t('The name of the file is not valid'), { variant: 'error',
            anchorOrigin: { horizontal: 'center', vertical: 'top' }, autoHideDuration: 2000 });
          return;
        }

    // 1️⃣ Initialize upload session
    await axios.post("/api/home/upload-init", { folderName, fileName: file.name, subFolder: isPrivate ? userId.toString(): ''   });

    console.log('Before status !!!');
    // 2️⃣ Get upload status
    const statusRes = await axios.get<UploadStatus>(
      `/api/home/upload-status?folderName=${folderName}&fileName=${file.name}&subFolder=${isPrivate ? userId.toString() : ""  }`
    );
    const uploadedChunks = new Set(statusRes.data.uploadedChunks || []);
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let completed = uploadedChunks.size;

    const chunkIndexes = Array.from(
      { length: totalChunks },
      (_, i) => i
    ).filter((i) => !uploadedChunks.has(i));

    setStatus(`Uploading ${chunkIndexes.length} missing chunks...`);

    // 3️⃣ Parallel upload worker
    const uploadQueue = async () => {
      while (chunkIndexes.length > 0) {
        const index = chunkIndexes.shift();
        if (index === undefined) return;

        await uploadChunk(file, index, totalChunks);
        completed++;
        setProgress(Math.round((completed / totalChunks) * 100));
      }
    };

    // Launch workers
    const workers = Array.from({ length: PARALLEL_LIMIT }, uploadQueue);
    await Promise.all(workers);

    // 4️⃣ Complete upload
    setStatus("Finalizing...");
    await axios.post("/api/home/upload-complete", { folderName, fileName: file.name, subFolder: isPrivate ? userId.toString() : '' });

    setStatus(`✅ ${t('Upload complete')}!`);
    setIsUploading(false);
  };

  return (
    <FormDialog open={open} maxWidth='sm' height='55vh'
      okText={''} cancelText='' title={`${t('Upload file')} ...`} onCancel={onClose}
      onClose={onClose} onOk={onClose}  >
      <Stack flexDirection='column'  >
        <Box sx={{ mt: 1, width: '100%' }} >
          <TextField select onChange={(e) => {
                setFileType(e.target.value);
                setStatus(``);
                setProgress(0);
              }
            } value={fileType}
            // sx={{ width: 'calc(100% - 8px)' }} 
            fullWidth
            id="fileType"
            label={t('Select file type')} >
            <MenuItem value="backend">{'Backend (KampuxAPI.zip)'}</MenuItem>
            <MenuItem value="frontend">{'Frontend (KampuxWEB.zip)'}</MenuItem>
            <MenuItem value="report">{'Report (xxxx.zip)'}</MenuItem>
            <MenuItem value="other">{'Other (xxxx.zip)'}</MenuItem>
          </TextField>
        </Box>
        
        <Box sx={{ mt: 1, width: '100%' }} >
          <FormControlLabel
            control={
              <Checkbox
                checked={isPrivate}
                onChange={(event) => { setIsPrivate(event.target.checked); }}
              />
            }
            label={t('File is only for you ?')}
          />
        </Box>
        <Box
          sx={{
            mt: 0.25,
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center', // centers everything horizontally
          }}
        >
          {/* Upload Button */}
          <Button
            variant="outlined"
            onClick={() => (document as any).getElementById("file-upload")?.click()}
            sx={{ mt: 1, whiteSpace: 'nowrap' }}
          >
            {t('Upload file')}
          </Button>

          {/* Hidden File Input */}
          <input
            type="file"
            id="file-upload"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          {/* Progress Bar */}
          <Box sx={{ mt: 2, width: '60%' }}>
            <progress value={progress} max="100" style={{ width: "100%" }} />
          </Box>

          {/* Status Text */}
          <Box sx={{ mt: 1, textAlign: 'center' }}>
            <div>{progress}%</div>
            <p>{status}</p>
          </Box>
        </Box>
      </Stack>
    </FormDialog>
  )
}
